import type Variant from '@modules/product/type/variant';
import PriceUnitEnum from '@modules/product/type/price-unit-enum';
import { pxToRem, useAppDisplayModeContext } from '@archipro-design/aria';
import {
    areaUnitLabel,
    coverageAreaToDisplay,
    priceUnitDisplay,
    useLocalisation,
    type MeasurementSystem,
} from '@archipro-website/localisation';
import { quantityStepNote } from '@modules/product/util/measured-price';

/** Metric labels as the page has always read them; imperial ones come from the localisation package. */
const METRIC_PRICE_UNIT_LABELS: Record<string, string> = {
    CubicMetre: 'cubic metre',
    LinearMetre: 'metre',
    SquareMetre: 'square metre',
    Box: 'box',
};

const priceUnitLabel = (
    priceUnit: string,
    measurementSystem: MeasurementSystem,
): string =>
    (measurementSystem === 'imperial'
        ? priceUnitDisplay(priceUnit, measurementSystem)?.label
        : undefined) ??
    METRIC_PRICE_UNIT_LABELS[priceUnit] ??
    priceUnit;

/** Square centimetres in one square metre; metric shows the stored area unrounded, as it always has. */
const SQUARE_CENTIMETRES_PER_SQUARE_METRE = 10_000;

/** Variant.CoverageArea (cm²) in the market's area unit, e.g. "4m²" or "43.06 sq ft". */
const formatCoverageArea = (
    squareCentimetres: number,
    measurementSystem: MeasurementSystem,
): string =>
    measurementSystem === 'imperial'
        ? `${coverageAreaToDisplay(squareCentimetres, measurementSystem)} ${areaUnitLabel(measurementSystem)}`
        : `${squareCentimetres / SQUARE_CENTIMETRES_PER_SQUARE_METRE}m²`;

interface Props {
    variant: Pick<Variant, 'PriceUnit' | 'QuantityPerUnit' | 'CoverageArea'>;
    isPriceRange?: boolean;
}

export const PriceUnit = ({ variant, isPriceRange }: Props) => {
    const { mobile } = useAppDisplayModeContext();
    const { measurementSystem } = useLocalisation().countryConfig;

    if (!variant.PriceUnit || variant.PriceUnit === PriceUnitEnum.Unit) {
        return <div></div>;
    }
    const text = `${!isPriceRange ? 'Price ' : ''}per ${priceUnitLabel(
        variant.PriceUnit,
        measurementSystem,
    )}`;
    if (variant.PriceUnit !== PriceUnitEnum.Box) {
        const stepNote = quantityStepNote(variant.PriceUnit, measurementSystem);
        return <span>{stepNote ? `${text}. ${stepNote}` : text}</span>;
    }

    const showUnitText =
        variant.QuantityPerUnit > 0 || variant.CoverageArea > 0;

    const unitText =
        (variant.QuantityPerUnit > 0
            ? ` Qty per box: ${variant.QuantityPerUnit}${mobile ? '' : '.'}`
            : '') +
        (variant.CoverageArea > 0
            ? `${mobile ? '\n' : ''}Coverage area: ${formatCoverageArea(
                  variant.CoverageArea,
                  measurementSystem,
              )}${mobile ? '' : '.'}`
            : '');

    return mobile ? (
        <span>
            {text}{' '}
            {showUnitText && (
                <MobileUnitText
                    variant={variant}
                    measurementSystem={measurementSystem}
                />
            )}
        </span>
    ) : (
        <span>
            {(unitText ? text + '.' + unitText : text).replace(/\.$/, '')}
        </span>
    );
};

const MobileUnitText = ({
    variant,
    measurementSystem,
}: {
    variant: Pick<Variant, 'PriceUnit' | 'QuantityPerUnit' | 'CoverageArea'>;
    measurementSystem: MeasurementSystem;
}) => {
    const coverageArea = (
        <>
            Coverage area:{' '}
            {formatCoverageArea(variant.CoverageArea, measurementSystem)}
        </>
    );
    return (
        <span>
            &#8211;{' '}
            {variant.QuantityPerUnit > 0 && (
                <>Qty per box: {variant.QuantityPerUnit}</>
            )}
            {variant.CoverageArea > 0 &&
                (variant.QuantityPerUnit > 0 ? (
                    <div style={{ marginTop: pxToRem(5) }}>{coverageArea}</div>
                ) : (
                    coverageArea
                ))}
        </span>
    );
};
